import { Check, Delete, ExpandMore, InfoOutlined, PostAdd } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AppTextField from 'components/UI/inputs/AppTextField';
import MultiSelect from 'components/MultiSelectorSearch';
import StandardsModal from 'components/StandardsModal';
import TextEditor from 'components/TextEditor';
import { cloneDeep, filter, get, map } from 'lodash';
import React, { useMemo, useState } from 'react';

type QuestionDraft = Record<string, unknown> & { type: string };
import {
  compareGradesForIllinoisCommonCore,
  getStateNameDisplayForCode,
  getStateNameFromCode,
  normalizeStateForComparison,
  statesMatch,
} from 'utils/lib/standardHelper';

type BookPage = { pageId?: string | number; NumberMasked?: string };

const AI_GRADED_TYPES = new Set([
  'essay',
  'picture',
  'audio',
  'video',
  'audioEssay',
  'audioToImage',
  'photoCollage',
]);

export type SuperAdminQuestionExtrasProps = {
  draft: QuestionDraft;
  onPatch: (patch: Partial<QuestionDraft>) => void;
  superAdminMode?: boolean;
  bookPages?: BookPage[];
  isStandardExamBook?: boolean;
  standards?: { abbr?: string; status?: boolean; state?: string; grade_level?: string }[];
  ebookGradeLevels?: { state?: string; grade_level?: string }[];
  enabledStateCodes?: string[];
  currentUserRole?: string | null;
};

function pageMatchesConfigId(page: BookPage | undefined, raw: unknown) {
  if (page == null || raw == null || raw === '') return false;
  const a = parseInt(String(page.pageId), 10);
  const b = parseInt(String(raw), 10);
  if (!Number.isNaN(a) && !Number.isNaN(b)) return a === b;
  return String(page.pageId) === String(raw);
}

function pageDisplayLabel(page: BookPage | undefined) {
  if (!page || typeof page !== 'object') return '';
  const masked = page.NumberMasked;
  if (masked != null && String(masked).trim() !== '') return String(masked);
  return page.pageId != null ? String(page.pageId) : '';
}

function hasQuestionAiGradingOptionalConfig(draft: QuestionDraft): boolean {
  if (String(draft.grade_prompt ?? '').trim() !== '') return true;
  const refs = draft.grade_page_ref;
  if (!Array.isArray(refs)) return false;
  return refs.some((id) => id != null && String(id).trim() !== '');
}

const questionAiGradingAccordionSx = {
  mt: 2,
  border: '1px solid #e2e8f0',
  borderRadius: '8px !important',
  overflow: 'hidden',
  '&:before': { display: 'none' },
} as const;

function GradePageRefFields({
  draft,
  onPatch,
  bookPages,
}: {
  draft: QuestionDraft;
  onPatch: (patch: Partial<QuestionDraft>) => void;
  bookPages: BookPage[];
}) {
  const refIds = Array.isArray(draft.grade_page_ref) ? draft.grade_page_ref : [];
  const selectedPages = refIds
    .map((id) => bookPages.find((p) => pageMatchesConfigId(p, id)))
    .filter(Boolean) as BookPage[];

  return (
    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={bookPages}
        value={selectedPages}
        onChange={(_e, newValue) => {
          const capped = newValue.slice(0, 3);
          onPatch({ grade_page_ref: capped.map((p) => p.pageId) });
        }}
        getOptionLabel={(option) => pageDisplayLabel(option)}
        isOptionEqualToValue={(option, value) =>
          Boolean(option && value && pageMatchesConfigId(option, value.pageId))
        }
        getOptionDisabled={(option) =>
          selectedPages.length >= 3 && !selectedPages.some((s) => pageMatchesConfigId(s, option.pageId))
        }
        renderInput={(params) => (
          <AppTextField {...params} label="Grade reference pages" placeholder="Up to 3 pages" size="small" />
        )}
      />
    </FormControl>
  );
}

function QuestionStandardsBlock({
  draft,
  onPatch,
  standards,
  ebookGradeLevels,
  enabledStateCodes,
}: {
  draft: QuestionDraft;
  onPatch: (patch: Partial<QuestionDraft>) => void;
  standards: { abbr?: string; status?: boolean; state?: string; grade_level?: string }[];
  ebookGradeLevels: { state?: string; grade_level?: string }[];
  enabledStateCodes: string[];
}) {
  const [standardInfoModal, setStandardInfoModal] = useState<{
    open: boolean;
    standardAbbrs: string[];
    stateStandard: unknown;
  }>({ open: false, standardAbbrs: [], stateStandard: null });

  const filteredStandardsOptions = useMemo(() => {
    let filteredByState = standards;
    if (enabledStateCodes && enabledStateCodes.length > 0) {
      filteredByState = filter(standards, (standard) => {
        const standardStatus = get(standard, 'status', false);
        if (standardStatus !== true) return false;
        const standardState = get(standard, 'state', '');
        const enabledStateNames = enabledStateCodes.map((code) => normalizeStateForComparison(code)).filter(Boolean);
        return enabledStateNames.some((enabledName) => statesMatch(standardState, enabledName));
      });
    } else {
      filteredByState = filter(standards, (standard) => get(standard, 'status', false) === true);
    }
    return map(filteredByState, (k) => k.abbr).filter(Boolean) as string[];
  }, [standards, enabledStateCodes]);

  const renderLabel = (value: string) => {
    const stateName = getStateNameDisplayForCode(value);
    if (stateName) return stateName;
    return value;
  };

  const handleStateStandardChange = (
    selectedValues: string[],
    standardObj: { standard_name?: string; grade?: unknown },
  ) => {
    const prev = draft.state_standard;
    const updated = Array.isArray(prev) ? cloneDeep(prev) : [];
    const targetStateName = standardObj?.standard_name || '';
    const existingIndex = updated.findIndex((s: { standard_name?: string }) => {
      const sState = typeof s === 'string' ? s : get(s, 'standard_name', '');
      return statesMatch(sState, targetStateName);
    });
    const nextRow = {
      standard_name: targetStateName,
      grade: standardObj?.grade ?? null,
      standard: Array.isArray(selectedValues) ? selectedValues : [],
    };
    if (existingIndex >= 0) {
      updated[existingIndex] = { ...updated[existingIndex], ...nextRow };
    } else {
      updated.push(nextRow);
    }
    onPatch({ state_standard: updated });
  };

  if (!Array.isArray(ebookGradeLevels) || ebookGradeLevels.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {ebookGradeLevels.map((gradeLevelItem, stateIndex) => {
        const stateCode = get(gradeLevelItem, 'state', '');
        const state = getStateNameFromCode(stateCode) || stateCode;
        const gradeLevel = get(gradeLevelItem, 'grade_level', '');
        if (!state) return null;
        const standardObj = { standard_name: state, grade: gradeLevel };
        const questionStateStandard = draft.state_standard;
        const existingStateStandard =
          questionStateStandard && Array.isArray(questionStateStandard)
            ? questionStateStandard.find((s: { standard_name?: string }) => {
                const sState = typeof s === 'string' ? s : get(s, 'standard_name', '');
                return statesMatch(sState, state);
              })
            : null;
        const standardArray = existingStateStandard
          ? Array.isArray((existingStateStandard as { standard?: unknown }).standard)
            ? (existingStateStandard as { standard: unknown[] }).standard
            : (existingStateStandard as { standard?: unknown }).standard
              ? [(existingStateStandard as { standard?: unknown }).standard]
              : []
          : [];
        const filterOptions =
          standards
            ?.filter((k) => {
              const standardStatus = get(k, 'status', false);
              if (standardStatus !== true) return false;
              const standardState = get(k, 'state', '');
              const standardGradeLevel = get(k, 'grade_level', '');
              if (!statesMatch(standardState, state)) return false;
              if (!gradeLevel) return true;
              const ebookGrades = String(gradeLevel).split(',').map((g) => g.trim()).filter(Boolean);
              if (ebookGrades.length === 0) return true;
              if (!standardGradeLevel) return true;
              const standardGrades = String(standardGradeLevel).split(',').map((g) => g.trim()).filter(Boolean);
              return ebookGrades.some((ebookGrade) =>
                standardGrades.some((standardGrade) =>
                  compareGradesForIllinoisCommonCore(ebookGrade, standardGrade, standardState),
                ),
              );
            })
            ?.map((k) => k.abbr)
            .filter(Boolean) || [];
        const selectedStandards = standardArray as string[];
        if (filterOptions.length === 0) return null;
        return (
          <Box key={stateIndex} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {renderLabel(state)}
              </Typography>
              {selectedStandards.length > 0 ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setStandardInfoModal({
                      open: true,
                      standardAbbrs: selectedStandards,
                      stateStandard: [{ ...standardObj, standard: selectedStandards }],
                    });
                  }}
                >
                  <InfoOutlined fontSize="small" color="info" />
                </IconButton>
              ) : null}
            </Box>
            <MultiSelect
              label=""
              options={filterOptions.length > 0 ? filterOptions : filteredStandardsOptions}
              selectedValue={selectedStandards}
              onSelect={(v) => {
                const selectedArray =
                  typeof v === 'string' ? (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []) : v || [];
                handleStateStandardChange(selectedArray, standardObj);
              }}
            />
          </Box>
        );
      })}
      <StandardsModal
        open={standardInfoModal.open}
        onClose={() => setStandardInfoModal({ open: false, standardAbbrs: [], stateStandard: null })}
        standardAbbrs={standardInfoModal.standardAbbrs}
        stateStandard={standardInfoModal.stateStandard}
        standards={standards}
        enabledStateCodes={enabledStateCodes}
        userRole="owe_admin"
      />
    </Box>
  );
}

export function SuperAdminQuestionExtras(props: SuperAdminQuestionExtrasProps) {
  const {
    draft,
    onPatch,
    superAdminMode,
    bookPages = [],
    isStandardExamBook,
    standards = [],
    ebookGradeLevels = [],
    enabledStateCodes = [],
  } = props;

  if (!superAdminMode) {
    return null;
  }

  const type = draft.type;
  const showAiGradingBlock =
    AI_GRADED_TYPES.has(type) || type === 'audioMultipleChoice' || type === 'audioEssay';

  const essayExtras =
    type === 'essay' ? (
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 1.5,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Expected response
          </Typography>
          <Button
            type="button"
            variant="contained"
            size="medium"
            startIcon={<PostAdd sx={{ fontSize: 22 }} />}
            onClick={() => {
              const arr = Array.isArray(draft.expected_answer) ? [...(draft.expected_answer as string[])] : [];
              arr.push('');
              onPatch({ expected_answer: arr });
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              letterSpacing: '0.02em',
              px: 2.25,
              py: 1,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 48%, #0369a1 100%)',
              boxShadow: '0 2px 10px rgba(14, 165, 233, 0.38)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 50%, #0284c7 100%)',
                boxShadow: '0 4px 16px rgba(14, 165, 233, 0.48)',
              },
            }}
          >
            Add expected
          </Button>
        </Box>
        {Array.isArray(draft.expected_answer) &&
          draft.expected_answer.length > 0 &&
          (draft.expected_answer as string[]).map((answer, answerIndex) => (
            <Box key={answerIndex} sx={{ mb: 2, position: 'relative' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption">Expected response ({answerIndex + 1})</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    const arr = (draft.expected_answer as string[]).filter((_, i) => i !== answerIndex);
                    onPatch({ expected_answer: arr });
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              <TextEditor
                handleChange={(value) => {
                  const arr = Array.isArray(draft.expected_answer) ? [...(draft.expected_answer as string[])] : [];
                  arr[answerIndex] = value;
                  onPatch({ expected_answer: arr });
                }}
                placeholder="Type expected response here..."
                value={answer || ''}
                autoFocus={false}
                disabled={false}
                containerClassName="question-ebook-text-field"
              />
            </Box>
          ))}
      </Box>
    ) : null;

  const questionAiGradingOptionalConfigured = hasQuestionAiGradingOptionalConfig(draft);

  const gradingExtras = showAiGradingBlock ? (
    <Accordion defaultExpanded={false} disableGutters elevation={0} sx={questionAiGradingAccordionSx}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2, bgcolor: '#f8fafc' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            AI grading (optional)
          </Typography>
          {questionAiGradingOptionalConfigured ? (
            <Check sx={{ color: 'success.main', fontSize: 22 }} aria-hidden />
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2, pt: 1, bgcolor: '#fafafa' }}>
        <GradePageRefFields draft={draft} onPatch={onPatch} bookPages={bookPages} />
        <AppTextField
          label="Grade prompt"
          multiline
          minRows={2}
          value={String(draft.grade_prompt ?? '')}
          onChange={(e) => onPatch({ grade_prompt: e.target.value })}
          placeholder="Optional prompt for AI grading..."
        />
      </AccordionDetails>
    </Accordion>
  ) : null;

  const standardExamExtras = isStandardExamBook ? (
    <Box sx={{ mt: 2 }}>
      <AppTextField
        label="Explanation"
        required
        multiline
        minRows={1}
        sx={{ mb: 2 }}
        value={String(draft.explanation ?? '')}
        onChange={(e) => onPatch({ explanation: e.target.value })}
      />
      <AppTextField
        label="Standard story"
        multiline
        minRows={2}
        value={String(draft.standard_story ?? '')}
        onChange={(e) => onPatch({ standard_story: e.target.value })}
      />
    </Box>
  ) : null;

  const perQuestionStandards =
    standards.length > 0 && ebookGradeLevels.length > 0 ? (
      <QuestionStandardsBlock
        draft={draft}
        onPatch={onPatch}
        standards={standards}
        ebookGradeLevels={ebookGradeLevels}
        enabledStateCodes={enabledStateCodes}
      />
    ) : null;

  return (
    <>
      {essayExtras}
      {gradingExtras}
      {perQuestionStandards}
      {standardExamExtras}
    </>
  );
}
